import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Logo } from './Logo';
import { RunningOrders } from './RunningOrders';
import { ScheduledOrders } from './ScheduledOrders';
type ProductionView = 'menu' | 'planned-orders' | 'running-orders';
type MenuProductionProps = {
  username: string;
  onLogout: () => void;
  initialView?: ProductionView;
};
type MenuAction = 'planned' | 'started';
type ProductionAction = {
  id: MenuAction;
  title: string;
  description: string;
};
const productionActions: ProductionAction[] = [{
  id: 'started',
  title: 'Aufträge',
  description: 'Laufende Produktionen anzeigen'
}, {
  id: 'planned',
  title: 'Geplante Aufträge',
  description: 'Nächste Arbeit vorbereiten'
}];
export function MenuProduction({
  username,
  onLogout,
  initialView = 'menu'
}: MenuProductionProps) {
  const [view, setView] = useState<ProductionView>(initialView);
  const [shouldLoadPlannedOrders, setShouldLoadPlannedOrders] = useState(false);
  const [shouldLoadRunningOrders, setShouldLoadRunningOrders] = useState(false);
  function handleAction(action: MenuAction) {
    if (action === 'planned') {
      setShouldLoadPlannedOrders(true);
      setView('planned-orders');
      return;
    }
    setShouldLoadRunningOrders(true);
    setView('running-orders');
  }
  function returnToMenu() {
    setView('menu');
    setShouldLoadPlannedOrders(false);
    setShouldLoadRunningOrders(false);
  }
  if (view === 'planned-orders') {
    return <ScheduledOrders onBack={returnToMenu} isInitialLoading={shouldLoadPlannedOrders} data-id="element-117" />;
  }
  if (view === 'running-orders') {
    return <RunningOrders onBack={returnToMenu} isInitialLoading={shouldLoadRunningOrders} data-id="element-118" />;
  }
  return <main className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-119">
      <header className="border-b border-border bg-surface px-5 py-4 sm:px-8 md:px-10 lg:px-14" data-id="element-120">
        <div className="flex items-center" data-id="element-121">
          <Logo size="lg" alt="Sonnentracht" className="h-[52px] w-auto sm:h-[60px]" data-id="element-122" />
        </div>
      </header>

      <section className="mx-auto flex min-h-[calc(100vh-93px)] max-w-6xl flex-col px-5 py-10 sm:px-8 md:justify-center md:px-10 lg:px-14" data-id="element-123">
        <motion.div className="mx-auto w-full max-w-3xl" initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} data-id="element-124">
          <div className="text-center" data-id="element-125">
            <p className="text-sm text-text-secondary" data-id="element-126">
              Willkommen zurück, {username}.
            </p>
            <h1 className="mt-2 font-heading text-4xl leading-tight sm:text-5xl" data-id="element-127">
              Produktion
            </h1>
          </div>

          <div className="mx-auto mt-10 grid max-w-[760px] rotate-[-12deg] grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-0" data-id="element-128">
            {productionActions.map((action, index) => {
            return <motion.button key={action.id} type="button" onClick={() => handleAction(action.id)} className={`group relative flex aspect-square w-full flex-col items-center justify-center px-9 text-center text-text-primary transition-transform focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-green ${index === 0 ? 'sm:ml-[calc(6.7%-6px)]' : 'sm:ml-[calc(6px-6.7%)]'}`} style={{
              clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
            }} whileHover={{
              y: -5,
              scale: 1.015
            }} whileTap={{
              scale: 0.98
            }} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.32,
              delay: index * 0.08,
              ease: 'easeOut'
            }} data-id="element-129">
                  <span aria-hidden="true" className="absolute inset-1 bg-brand-green transition-colors group-hover:bg-brand-green-dark" style={{
                clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
              }} data-id="element-130" />
                  <span className="relative rotate-[12deg] font-heading text-2xl leading-tight sm:text-3xl" data-id="element-131">
                    {action.title}
                  </span>
                  <span className="relative mt-2 rotate-[12deg] text-sm text-text-primary/75" data-id="element-132">
                    {action.description}
                  </span>
                </motion.button>;
          })}

            <motion.button type="button" onClick={onLogout} className="group relative mx-auto flex aspect-square w-full max-w-[360px] flex-col items-center justify-center px-9 text-center text-text-primary transition-transform focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-green sm:col-span-2 sm:-mt-[98px]" style={{
            clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
          }} whileHover={{
            y: -5,
            scale: 1.015
          }} whileTap={{
            scale: 0.98
          }} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.32,
            delay: 0.18,
            ease: 'easeOut'
          }} data-id="element-133">
              <span aria-hidden="true" className="absolute inset-1 bg-brand-green transition-colors group-hover:bg-brand-green-dark" style={{
              clipPath: 'polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%)'
            }} data-id="element-134" />
              <span className="relative rotate-[12deg] font-heading text-2xl sm:text-3xl" data-id="element-135">
                Abmelden
              </span>
              <span className="relative mt-2 rotate-[12deg] text-sm text-text-secondary" data-id="element-136">
                Sicher zur Anmeldung zurückkehren
              </span>
            </motion.button>
          </div>
        </motion.div>
      </section>
    </main>;
}